export type InboxSampleStatus = "active" | "human_takeover" | "closed";

export interface InboxSampleConversation {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  customer_contact_phone: string | null;
  customer_status: "new" | "active" | "at-risk" | "dormant" | "lapsed" | "vip";
  customer_phone: string;
  status: InboxSampleStatus;
  created_at: string;
  updated_at: string;
  channel: "whatsapp" | "telegram" | "instagram" | "facebook_messenger" | "line";
  channel_user_id: string;
  last_message: string;
  last_message_time: string;
  message_count: number;
  is_sample: true;
}

export interface InboxSampleMessage {
  id: string;
  conversation_id: string;
  sender_type: "customer" | "ai" | "human_owner";
  content: string;
  created_at: string;
}

const minutesBefore = (now: Date, minutes: number) =>
  new Date(now.getTime() - minutes * 60 * 1000).toISOString();

const daysBefore = (now: Date, days: number, minutes = 0) =>
  minutesBefore(now, days * 24 * 60 + minutes);

/**
 * A lightweight, local-only inbox preview for the same first-run state that
 * shows sample customers. Nothing here is persisted or sent to a channel.
 */
export function createInboxSamples(now = new Date()) {
  const conversations: InboxSampleConversation[] = [
    {
      id: "sample-inbox-avery",
      customer_id: "sample-customer-1",
      customer_name: "Avery Tan",
      customer_email: "avery.tan@example.com",
      customer_contact_phone: "+65 6000 0001",
      customer_status: "vip",
      customer_phone: "+65 6000 0001",
      status: "human_takeover",
      created_at: daysBefore(now, 2),
      updated_at: minutesBefore(now, 3),
      channel: "whatsapp",
      channel_user_id: "+6560000001",
      last_message: "Could I move tomorrow's appointment to 3 pm?",
      last_message_time: minutesBefore(now, 3),
      message_count: 5,
      is_sample: true,
    },
    {
      id: "sample-inbox-sofia",
      customer_id: "sample-customer-3",
      customer_name: "Sofia Lee",
      customer_email: "sofia.lee@example.com",
      customer_contact_phone: null,
      customer_status: "active",
      customer_phone: "@sofia.lee",
      status: "active",
      created_at: daysBefore(now, 8),
      updated_at: minutesBefore(now, 18),
      channel: "instagram",
      channel_user_id: "sofia.lee",
      last_message: "Perfect, thank you! See you this weekend.",
      last_message_time: minutesBefore(now, 18),
      message_count: 5,
      is_sample: true,
    },
    {
      id: "sample-inbox-ethan",
      customer_id: "sample-customer-6",
      customer_name: "Ethan Ng",
      customer_email: "ethan.ng@example.com",
      customer_contact_phone: "+65 6000 0006",
      customer_status: "lapsed",
      customer_phone: "583104822",
      status: "active",
      created_at: daysBefore(now, 5),
      updated_at: minutesBefore(now, 46),
      channel: "telegram",
      channel_user_id: "583104822",
      last_message: "Do you have any evening slots next Tuesday?",
      last_message_time: minutesBefore(now, 46),
      message_count: 4,
      is_sample: true,
    },
    {
      id: "sample-inbox-chloe",
      customer_id: "sample-customer-7",
      customer_name: "Chloe Goh",
      customer_email: "chloe.goh@example.com",
      customer_contact_phone: "+65 6000 0007",
      customer_status: "active",
      customer_phone: "chloe.goh",
      status: "active",
      created_at: daysBefore(now, 12),
      updated_at: daysBefore(now, 1, 72),
      channel: "facebook_messenger",
      channel_user_id: "chloe.goh",
      last_message: "Can I book the same service for a friend too?",
      last_message_time: daysBefore(now, 1, 72),
      message_count: 3,
      is_sample: true,
    },
    {
      id: "sample-inbox-noah",
      customer_id: "sample-customer-4",
      customer_name: "Noah Wong",
      customer_email: "noah.wong@example.com",
      customer_contact_phone: "+65 6000 0004",
      customer_status: "at-risk",
      customer_phone: "noah-wong",
      status: "closed",
      created_at: daysBefore(now, 20),
      updated_at: daysBefore(now, 3, 18),
      channel: "line",
      channel_user_id: "noah-wong",
      last_message: "That's all sorted, thanks for your help.",
      last_message_time: daysBefore(now, 3, 18),
      message_count: 4,
      is_sample: true,
    },
  ];

  const messagesByConversation: Record<string, InboxSampleMessage[]> = {
    "sample-inbox-avery": [
      { id: "avery-1", conversation_id: "sample-inbox-avery", sender_type: "customer", content: "Hi! I have an appointment tomorrow morning.", created_at: minutesBefore(now, 9) },
      { id: "avery-2", conversation_id: "sample-inbox-avery", sender_type: "ai", content: "Hi Avery! I found your booking for tomorrow at 10:00 am. How can I help?", created_at: minutesBefore(now, 8) },
      { id: "avery-3", conversation_id: "sample-inbox-avery", sender_type: "customer", content: "Could I move tomorrow's appointment to 3 pm?", created_at: minutesBefore(now, 6) },
      { id: "avery-4", conversation_id: "sample-inbox-avery", sender_type: "ai", content: "I can help with that. There is a 3:00 pm slot available. I've paused here so the team can confirm the change with you.", created_at: minutesBefore(now, 5) },
      { id: "avery-5", conversation_id: "sample-inbox-avery", sender_type: "customer", content: "Great, 3 pm works for me!", created_at: minutesBefore(now, 3) },
    ],
    "sample-inbox-sofia": [
      { id: "sofia-1", conversation_id: "sample-inbox-sofia", sender_type: "customer", content: "Hi, I saw your weekend opening on Instagram. Is it still available?", created_at: minutesBefore(now, 26) },
      { id: "sofia-2", conversation_id: "sample-inbox-sofia", sender_type: "ai", content: "It is! I can hold Saturday at 11:30 am for you while we complete the booking.", created_at: minutesBefore(now, 24) },
      { id: "sofia-3", conversation_id: "sample-inbox-sofia", sender_type: "customer", content: "That timing works.", created_at: minutesBefore(now, 22) },
      { id: "sofia-4", conversation_id: "sample-inbox-sofia", sender_type: "ai", content: "You're booked for Saturday at 11:30 am. A confirmation has been sent to your email.", created_at: minutesBefore(now, 20) },
      { id: "sofia-5", conversation_id: "sample-inbox-sofia", sender_type: "customer", content: "Perfect, thank you! See you this weekend.", created_at: minutesBefore(now, 18) },
    ],
    "sample-inbox-ethan": [
      { id: "ethan-1", conversation_id: "sample-inbox-ethan", sender_type: "customer", content: "Do you have any evening slots next Tuesday?", created_at: minutesBefore(now, 52) },
      { id: "ethan-2", conversation_id: "sample-inbox-ethan", sender_type: "ai", content: "Yes — 6:00 pm and 7:30 pm are both available next Tuesday. Which would you prefer?", created_at: minutesBefore(now, 50) },
      { id: "ethan-3", conversation_id: "sample-inbox-ethan", sender_type: "customer", content: "Let's do 6 pm, please.", created_at: minutesBefore(now, 48) },
      { id: "ethan-4", conversation_id: "sample-inbox-ethan", sender_type: "ai", content: "Done — I've reserved Tuesday at 6:00 pm for you.", created_at: minutesBefore(now, 46) },
    ],
    "sample-inbox-chloe": [
      { id: "chloe-1", conversation_id: "sample-inbox-chloe", sender_type: "customer", content: "Can I book the same service for a friend too?", created_at: daysBefore(now, 1, 78) },
      { id: "chloe-2", conversation_id: "sample-inbox-chloe", sender_type: "ai", content: "Absolutely. I can help find two nearby slots or send your friend a booking link.", created_at: daysBefore(now, 1, 75) },
      { id: "chloe-3", conversation_id: "sample-inbox-chloe", sender_type: "customer", content: "A booking link would be perfect.", created_at: daysBefore(now, 1, 72) },
    ],
    "sample-inbox-noah": [
      { id: "noah-1", conversation_id: "sample-inbox-noah", sender_type: "customer", content: "I need to update the phone number on my booking.", created_at: daysBefore(now, 3, 28) },
      { id: "noah-2", conversation_id: "sample-inbox-noah", sender_type: "ai", content: "Of course. Please send the new number and I'll update the booking details.", created_at: daysBefore(now, 3, 25) },
      { id: "noah-3", conversation_id: "sample-inbox-noah", sender_type: "customer", content: "Updated — I received the new confirmation.", created_at: daysBefore(now, 3, 20) },
      { id: "noah-4", conversation_id: "sample-inbox-noah", sender_type: "human_owner", content: "That's all sorted, thanks for your help.", created_at: daysBefore(now, 3, 18) },
    ],
  };

  return { conversations, messagesByConversation };
}
